import { useEffect, useState } from 'react';
import { Copy, Download, Upload } from 'lucide-react';
import { toast } from 'react-toastify';
import { settingsAPI } from '../../../config/settings-api';
import { API_CONFIG } from '../../../config/api';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { IS_ALFAJR } from '../../../constants/general';
import { helpers } from '../../../utils/helpers';
import './AdminApp.css';

const ADMIN_APP_ICON = IS_ALFAJR
  ? '/logos/admin-app-icon-alfajr.png'
  : '/logos/admin-app-icon.png';
const ADMIN_APP_NAME = IS_ALFAJR ? 'Al Fajr Admin App' : 'AbleMart Admin App';

const AdminApp = ({ adminData }) => {
  const canView = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_ADMIN_APP);
  const canManage = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.MANAGE_ADMIN_APP);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [adminAppVersion, setAdminAppVersion] = useState('');
  const [adminAppLink, setAdminAppLink] = useState('');
  const [adminAppUpdatedAt, setAdminAppUpdatedAt] = useState(null);
  const [versionInput, setVersionInput] = useState('');
  const [apkFile, setApkFile] = useState(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadBytes, setUploadBytes] = useState({ loaded: 0, total: 0 });

  const formatBytes = (bytes) => {
    if (!bytes || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024;
      unit += 1;
    }
    return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
  };

  const absoluteApkLink = (() => {
    if (!adminAppLink) return '';
    if (adminAppLink.startsWith('http')) return adminAppLink;
    try {
      const apiBase = API_CONFIG.BASE_URL || '';
      const siteBase = apiBase.replace(/\/api\/?$/, '');
      return `${siteBase}${adminAppLink.startsWith('/') ? '' : '/'}${adminAppLink}`;
    } catch {
      return adminAppLink;
    }
  })();

  const updatedOnLabel = adminAppUpdatedAt
    ? helpers.formatQatarTime(adminAppUpdatedAt)
    : null;

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getAllGeneralConfigs();
      if (response.success && response.data) {
        setAdminAppVersion(response.data.adminAppVersion || '');
        setAdminAppLink(response.data.adminAppLink || '');
        setAdminAppUpdatedAt(response.data.adminAppUpdatedAt || null);
      } else {
        toast.error(response.msg || 'Failed to load admin app settings');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load admin app settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchConfig();
    } else {
      setLoading(false);
    }
  }, [canView]);

  const openUploadModal = () => {
    setVersionInput(adminAppVersion || '');
    setApkFile(null);
    setUploadPercent(0);
    setUploadBytes({ loaded: 0, total: 0 });
    setShowUploadModal(true);
  };

  const closeUploadModal = () => {
    if (saving) return;
    setShowUploadModal(false);
    setApkFile(null);
    setUploadPercent(0);
    setUploadBytes({ loaded: 0, total: 0 });
  };

  const handleCopyLink = async () => {
    if (!absoluteApkLink) return;
    try {
      await navigator.clipboard.writeText(absoluteApkLink);
      toast.success('App link copied');
    } catch (err) {
      console.error(err);
      toast.error('Failed to copy link');
    }
  };

  const handleUpload = async () => {
    const version = versionInput.trim();
    if (!version) {
      toast.error('Please enter a version number');
      return;
    }
    if (!apkFile) {
      toast.error('Please select an APK file');
      return;
    }
    const ext = apkFile.name.split('.').pop()?.toLowerCase();
    if (ext !== 'apk') {
      toast.error('Only .apk files are allowed');
      return;
    }

    try {
      setSaving(true);
      setUploadPercent(0);
      setUploadBytes({ loaded: 0, total: apkFile.size || 0 });
      const response = await settingsAPI.uploadAdminApp({
        version,
        apkFile,
        onProgress: ({ loaded, total, percent }) => {
          setUploadPercent(percent);
          setUploadBytes({ loaded, total });
        },
      });
      if (response.success) {
        toast.success(response.msg || 'Admin app updated');
        setAdminAppVersion(response.data?.adminAppVersion || version);
        setAdminAppLink(response.data?.adminAppLink || '');
        setAdminAppUpdatedAt(
          response.data?.adminAppUpdatedAt || new Date().toISOString()
        );
        setShowUploadModal(false);
        setApkFile(null);
        setUploadPercent(0);
        setUploadBytes({ loaded: 0, total: 0 });
      } else {
        toast.error(response.msg || 'Failed to upload admin app');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload admin app');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-app-page">
      {!canView ? (
        <div className="admin-app-loading">You do not have permission to view Admin App.</div>
      ) : (
        <>
      <div className="admin-app-header">
        <div>
          <h1>Admin App</h1>
          <p>Upload and manage the Android admin APK for staff download</p>
        </div>
        {canManage && (
          <button className="admin-app-primary-btn" onClick={openUploadModal}>
            <Upload size={16} />
            Upload New APK
          </button>
        )}
      </div>

      {loading ? (
        <div className="admin-app-loading">Loading...</div>
      ) : (
        <div className="admin-app-card">
          <div className="admin-app-icon-wrap">
            <img src={ADMIN_APP_ICON} alt={ADMIN_APP_NAME} />
          </div>
          <div className="admin-app-meta">
            <h2>{ADMIN_APP_NAME}</h2>
            <p>
              <strong>Version:</strong> {adminAppVersion || 'Not set'}
            </p>
            <div className="admin-app-link-row">
              <strong>App link:</strong>
              {absoluteApkLink ? (
                <div className="admin-app-link-value">
                  <a href={absoluteApkLink} target="_blank" rel="noreferrer">
                    {absoluteApkLink}
                  </a>
                  <button
                    type="button"
                    className="admin-app-copy-btn"
                    onClick={handleCopyLink}
                    title="Copy app link"
                    aria-label="Copy app link"
                  >
                    <Copy size={15} />
                  </button>
                </div>
              ) : (
                <span>Not uploaded</span>
              )}
            </div>
            <p>
              <strong>Updated on:</strong>{' '}
              {updatedOnLabel || '—'}
            </p>
          </div>
          <div className="admin-app-actions">
            {absoluteApkLink && (
              <a className="admin-app-secondary-btn" href={absoluteApkLink} download>
                <Download size={16} />
                Download current APK
              </a>
            )}
          </div>
        </div>
      )}

      {showUploadModal && canManage && (
        <div className="admin-app-modal-overlay" onClick={closeUploadModal}>
          <div className="admin-app-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-app-modal-header">
              <h3>Upload Admin App APK</h3>
              <button className="admin-app-close" onClick={closeUploadModal} disabled={saving}>
                ×
              </button>
            </div>
            <div className="admin-app-modal-body">
              <p className="admin-app-modal-hint">
                Uploading a new APK replaces the previous file in <code>public/adminapp</code> and
                updates the version/link.
              </p>
              <label className="admin-app-label">Version number</label>
              <input
                type="text"
                className="admin-app-input"
                placeholder="e.g. 1.0.3"
                value={versionInput}
                onChange={(e) => setVersionInput(e.target.value)}
                disabled={saving}
              />
              <label className="admin-app-label">APK file</label>
              <input
                type="file"
                accept=".apk,application/vnd.android.package-archive"
                className="admin-app-file"
                onChange={(e) => setApkFile(e.target.files?.[0] || null)}
                disabled={saving}
              />
              {apkFile && (
                <p className="admin-app-file-name">
                  Selected: {apkFile.name}
                  {apkFile.size ? ` (${formatBytes(apkFile.size)})` : ''}
                </p>
              )}
              {saving && (
                <div className="admin-app-upload-progress">
                  <div className="admin-app-upload-progress-header">
                    <span>
                      {uploadPercent >= 100
                        ? 'Processing…'
                        : `Uploading… ${uploadPercent}%`}
                    </span>
                    {uploadBytes.total > 0 && (
                      <span>
                        {formatBytes(uploadBytes.loaded)} / {formatBytes(uploadBytes.total)}
                      </span>
                    )}
                  </div>
                  <div
                    className="admin-app-upload-progress-track"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={uploadPercent}
                  >
                    <div
                      className="admin-app-upload-progress-fill"
                      style={{ width: `${Math.max(uploadPercent, 2)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="admin-app-modal-footer">
              <button className="admin-app-secondary-btn" onClick={closeUploadModal} disabled={saving}>
                Cancel
              </button>
              <button className="admin-app-primary-btn" onClick={handleUpload} disabled={saving}>
                {saving
                  ? (uploadPercent >= 100 ? 'Processing…' : `Uploading ${uploadPercent}%`)
                  : 'Upload & Save'}
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default AdminApp;
